/**
 * `InstallVerifyStep` — ADR-0049 step 2 (Install + verify). The
 * commitment step: the merchant picks an install method, pastes the
 * snippet (or grants OAuth on a store of record), and proves
 * ownership. Verify is MANDATORY — there is no Skip-Verify affordance
 * (ADR-0049 §"Defect 3"); the proof-check itself advances the flow via
 * `useProofCheck.onVerified → onNext()`.
 *
 * Composition (top → bottom):
 *   - `NumLabel` "Step 3 · Install and verify" — the only step kicker.
 *   - Serif h2 / sans body intro framing the install + proof
 *     (`Install Sill, then prove you own <domain>.`).
 *   - The detection-result tile (DETECTING / AUTO-DETECTION UNAVAILABLE /
 *     DETECTION FAILED / DETECTED PLATFORM) — preserved verbatim from
 *     the prior `InstallAndSkillsStep` so the install screen still
 *     surfaces what we saw. Detection is a hint, never a gate (every
 *     tab stays clickable).
 *   - `InstallMethodTabs` — ADR-0050 §1.1 narrows this to 3 peer tabs
 *     (`script_tag`, `wordpress`, `shopify`); the prior `custom_embed`
 *     peer is now a disclosed fallback inside the script-tag panel.
 *     The shared `useProofCheck` state is threaded into the tabs so
 *     the script-tag panel renders the proof-token-bearing snippet
 *     AND the inline Verify control next to Copy. For a detected
 *     Shopify site the Shopify tab is preselected and the Shopify
 *     connect lives ONLY here — no separate "RECOMMENDED FOR SHOPIFY"
 *     card above (ADR-0054 §0 declutter — the duplicate panel + double
 *     "Connecting…" surface was the founder-flagged regression).
 *   - `ProofStatusPanel` — slimmed per ADR-0050 §1.2. The snippet
 *     textarea + duplicate Copy + duplicate Verify are removed; what
 *     remains is the load/error state, the `.well-known` fallback
 *     affordance (when the homepage scan misses), and the read-only
 *     status line. The shared `useProofCheck` state is passed in.
 *     ADR-0054 §0 — this panel only renders when the active install
 *     tab is a snippet method (`script_tag` / `wordpress`); on the
 *     `shopify` tab the proof IS the OAuth connect, so a separate
 *     "PROVE DOMAIN OWNERSHIP" surface is irrelevant and confusing.
 *   - Stripe is NOT shown here — it moved to Settings → Integrations
 *     (ADR-0054 §0). Stripe Connect is conceptually independent of
 *     domain-proof and was cluttering the verify step.
 *
 * No Continue button. The proof check itself advances the flow.
 *
 * NO `ProtocolStackSection` render here — it moves to step 4
 * (Complete) per ADR-0049.
 */

import { motion } from 'framer-motion';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { DetectPlatformResult } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useSites } from '../../app/providers.js';
import { useProofCheck } from '../../hooks/useProofCheck.js';
import { useShopifyOauthReturn } from '../../hooks/useShopifyOauthReturn.js';
import { useSiteScan } from '../../hooks/useSiteScan.js';
import { deriveShopFromDomain } from '../../lib/shopify-install.js';

import { useC } from '../../lib/theme.js';
import { Card, NumLabel } from '../../components/atoms/index.js';
import { ShopifyManualInstallPanel } from '../sites/ShopifyManualInstallPanel.js';
import {
  InstallMethodTabs,
  preselectTab,
  type InstallMethod,
  type InstallPresentation,
} from './InstallMethodTabs.js';
import { InviteDeveloperPanel } from './InviteDeveloperPanel.js';
import { ProofStatusPanel } from './ProofStatusPanel.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type DetectionState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'detected'; result: DetectPlatformResult }
  | { kind: 'error'; message: string }
  | { kind: 'oauth_unavailable' };

export interface InstallVerifyStepProps {
  /** Called once the proof check succeeds. Advances the machine to
   *  step 3 (Complete). */
  onNext: () => void;
  /** ADR-0169 — deep-link into the Live Agent view (`'simulation'`)
   *  for the pre-install demo moment. Optional to preserve existing
   *  callers/tests; the card renders only when wired. */
  onWatchDemo?: () => void;
  /** ADR-0197 — override the install-picker presentation. When omitted,
   *  the step reads `VITE_INSTALL_MODEL` / `VITE_INSTALL_REFRAME`
   *  (default `'model'`; `'false'` / `'tabs'` → classic four-peer-tabs
   *  picker, the revert valve). Tests that assert on classic tab
   *  structure pin this to `'tabs'`. */
  installPresentation?: InstallPresentation;
}

/**
 * ADR-0197 — read the build-time flag that toggles the install picker
 * presentation. Kept private to this module (the only place
 * VITE_INSTALL_MODEL is consulted; every other surface receives the
 * resolved mode as a prop).
 *
 * Default is `'tabs'` — the tabs-on-top layout with the recommended tab
 * pre-selected + badged (founder 2026-07-28: the method tabs must be
 * visible at all times, not tucked behind a "See all options"
 * disclosure). Set `VITE_INSTALL_MODEL='model'` to opt into the
 * detection-driven no-tabs "model" presentation (kept as a revert
 * valve / experiment).
 */
function envInstallPresentation(): InstallPresentation {
  const raw = import.meta.env['VITE_INSTALL_MODEL'] as string | undefined;
  const trimmed = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return trimmed === 'model' ? 'model' : 'tabs';
}

export function InstallVerifyStep({
  onNext,
  onWatchDemo,
  installPresentation,
}: InstallVerifyStepProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite, setActiveSite, updateSite } = useSites();
  // ADR-0197 — presentation resolves from the prop override (tests) or
  // the env-flag default (build-time). `'model'` post-ADR-0197.
  const effectivePresentation: InstallPresentation =
    installPresentation ?? envInstallPresentation();
  const [detection, setDetection] = useState<DetectionState>({ kind: 'idle' });
  // ADR-0052 §4.2.1 — initial tab honors the suffix floor synchronously
  // so the first paint already has the Stripe per-tab kicker AND the
  // `InstallMethodTabs` initial selection agreeing on `shopify` for a
  // `*.myshopify.{com,io}` site. `deriveShopFromDomain` is a pure regex
  // check, safe inside a `useState` initializer. Non-myshopify sites
  // start on the universal `script_tag` default until detection lands.
  const [activeTab, setActiveTab] = useState<InstallMethod>(() =>
    activeSite !== null && deriveShopFromDomain(activeSite.domain) !== null
      ? 'shopify'
      : 'script_tag',
  );

  // ADR-0052 §4.2.1 client-side Shopify floor — a `*.myshopify.{com,io}`
  // domain is an UNFORGEABLE Shopify signal the client already holds
  // before any detect-platform round-trip resolves (the server treats
  // the same suffix as a high-confidence Shopify result; see
  // services/api/src/sites/detect-platform.ts). Treating the suffix as
  // authoritative means the install/verify UI is NEVER worse than what
  // the domain itself tells us — a transient detect-platform failure
  // can't leave the merchant on a "DETECTION FAILED / unknown" tile for
  // a domain that is provably Shopify.
  //
  // Precedence (load-bearing):
  //   effectivePlatform =
  //     deriveShopFromDomain(domain) ? 'shopify' : (api detection)
  //
  // The detect-platform call STILL runs and STILL informs every
  // non-myshopify case (custom-domain Shopify, WooCommerce, WordPress,
  // Custom cart). This floor only applies to the myshopify suffix.
  const apiDetectedPlatform: DetectPlatformResult['platform'] | null =
    detection.kind === 'detected' ? detection.result.platform : null;
  const shopifyFromDomain: string | null =
    activeSite !== null ? deriveShopFromDomain(activeSite.domain) : null;
  const detectedPlatform: DetectPlatformResult['platform'] | null =
    shopifyFromDomain !== null ? 'shopify' : apiDetectedPlatform;
  const isShopifyBySuffix = shopifyFromDomain !== null;

  // ADR-0050 §1.2 — single instance of the proof-check hook for Step 3.
  // The state is shared between `InstallMethodTabs` (where the inline
  // Verify control lives next to the snippet) and `ProofStatusPanel`
  // (where the load/error state + `.well-known` fallback live). Both
  // consumers see the SAME state object; the GET /proof-token call
  // fires exactly once per mount.
  //
  // ADR-0052 §2.4 — pass the detected platform so the friendly-miss
  // mapping can surface the Shopify password-page branch + the
  // Connect Shopify CTA on a homepage-miss for a detected-Shopify site.
  // The `detectedPlatform` here is the EFFECTIVE platform (suffix-
  // derived floor applied) so the friendly-miss steers the merchant to
  // OAuth even if detect-platform failed for a myshopify domain.
  const proofCheck = useProofCheck({
    onVerified: onNext,
    detectedPlatform,
  });

  // ADR-0052 §4.2.3 — observe the post-Shopify-OAuth return state. The
  // shared hook polls `getConnectorStatus(site_id, 'shopify')` for up
  // to 30s once the URL carries `?connected=shopify`. The advance
  // effect below uses the poll's `'connected'` terminal AS A TRIGGER to
  // re-fetch the site and CONFIRM the server-side `site.status` has
  // flipped — the URL flag is NEVER authoritative on its own. Mirrors
  // the ADR-0050 Stripe server-truth lesson.
  const shopifyReturn = useShopifyOauthReturn(
    activeSite !== null ? activeSite.site_id : null,
  );

  // 2026-10-27 engin8.io launch bug — the live detect-platform fetch has
  // a hard wall-clock deadline (see services/api/.../platform-detect.ts
  // `FETCH_TIMEOUT_MS`), and a slow WordPress origin trips it before any
  // signals are read. The readiness scan (`siteScan.scan.detected_platform`)
  // has already classified the site correctly at this point in onboarding
  // — it ran at sill.so email capture and SkillsStep polls the same hook
  // — so we read it here as a FALLBACK the live detection can defer to
  // when it returns unknown / other_known / fails. The hook polls while
  // the scan is still running; when it lands late the memoized `detected`
  // below updates and `InstallMethodTabs`'s re-pick effect re-selects
  // the right tab (still never overriding a merchant tab click; the
  // merchant-authority ref inside the tabs is the gate).
  const siteScan = useSiteScan(activeSite?.site_id);

  // Once-per-mount guard so we never call `onNext()` more than once
  // even if the poll re-fires or the site refresh races.
  const advancedRef = useRef(false);

  // ADR-0056 — when the Shopify OAuth callback returns a verified site
  // but the ScriptTag auto-install did NOT succeed (`scope_denied` /
  // `pending_retry` / `pending`), render the manual-install panel
  // inline and HOLD the auto-advance. The merchant sees the 5-step
  // theme.liquid instructions + the hash-form snippet here, in the
  // flow, instead of being kicked to a "you're live" completion that
  // records nothing (the storefront has no embed).
  //
  // `null` while we haven't checked; `'installed'` lets the advance
  // run; anything else surfaces the manual panel.
  const [shopifyEmbedStatus, setShopifyEmbedStatus] = useState<
    | 'installed'
    | 'scope_denied'
    | 'pending_retry'
    | 'pending'
    | 'not_applicable'
    | null
  >(null);
  // Merchant clicked "continue anyway" on the manual-install panel.
  // Lets them advance past the gate without a confirmed first ping —
  // they may have pasted the snippet but Shopify CDN caches CSS/JS
  // for a few minutes, and a forced wait would be hostile.
  const [manualContinueAcknowledged, setManualContinueAcknowledged] =
    useState(false);
  // "I added the embed — check now" loading + last-result-message
  // shape mirrored from SiteCard. Wired into the manual panel.
  const [manualChecking, setManualChecking] = useState(false);
  const [manualCheckMessage, setManualCheckMessage] = useState<
    string | undefined
  >(undefined);

  // Fire detection once per active-site mount. The endpoint is
  // server-side idempotent (overwrites the hint in place), so a
  // re-mount on theme toggle re-fires without side effect. Detection
  // is a HINT — even when this fails / 503s, the tabs render and stay
  // clickable.
  //
  // ADR-0052 §4.2.1 client-side Shopify floor — when the site's domain
  // is `*.myshopify.{com,io}`, preselect the Shopify tab IMMEDIATELY
  // (before the detect-platform round-trip resolves) AND keep it
  // preselected on any subsequent detection failure. The suffix is
  // authoritative; we never let an API timeout regress the UI to a tab
  // worse than what the domain already told us.
  useEffect(() => {
    if (activeSite === null) return;
    let cancelled = false;
    setDetection({ kind: 'loading' });
    void (async (): Promise<void> => {
      try {
        const result = await api.sites.detectPlatform(activeSite.site_id);
        if (cancelled) return;
        setDetection({ kind: 'detected', result });
        // Effective result for preselect: a `*.myshopify.{com,io}`
        // suffix forces the Shopify tab regardless of what the API
        // returned. Non-myshopify domains pass the raw result through.
        const effective: DetectPlatformResult = isShopifyBySuffix
          ? { ...result, platform: 'shopify' }
          : result;
        setActiveTab(preselectTab(effective));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'service_unavailable') {
          setDetection({ kind: 'oauth_unavailable' });
          return;
        }
        setDetection({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [activeSite, api, isShopifyBySuffix]);

  // ADR-0052 §4.2.2 — OAuth → onboarding-advance wire. The Shopify
  // callback (services/api/.../oauth-shopify.ts) already records the
  // `site_ownership_proof(proof_method='oauth_shopify')` row and flips
  // `site.status` to `discovery_active`/`transactional_active` inside
  // the same tenant tx, then redirects to
  // `/sites/{site_id}?connected=shopify`. The onboarding shell's
  // existing `resumeStepFor` correctly maps those statuses to step 4,
  // but the resume effect only re-fires on `site_id` CHANGE; the same
  // site coming back from OAuth therefore never triggered an advance.
  //
  // Fix: when `?connected=shopify` is on the URL AND we're mounted on
  // step 3 for that site, refresh the server's `site` row. If the
  // status has flipped to a verified tier (the load-bearing
  // condition — the server-side proof row + status transition are the
  // canonical authority), advance. The URL flag alone is not enough;
  // a merchant manually appending it gains nothing because the server
  // `site.status` for an unverified site is still `pending_proof`.
  useEffect(() => {
    if (advancedRef.current) return;
    if (!shopifyReturn.returnedFromOauth) return;
    if (activeSite === null) return;

    // ADR-0056 — verified-but-embed-not-installed gate. The Shopify
    // OAuth proof verifies ownership, but the post-OAuth ScriptTag
    // auto-install can fail (scope_denied / blocked on OS-2.0 themes).
    // When that happens we MUST NOT silently advance to "you're live"
    // because the storefront has no embed and nothing records.
    // Instead we stop the auto-advance, fetch the connector status,
    // and render the manual-install panel inline below. The merchant
    // either pastes the snippet and the InstallDetector polling
    // catches the first ping, OR they click "I added it — continue"
    // and we advance on their explicit acknowledgement.
    const verifiedNow =
      activeSite.status === 'discovery_active'
      || activeSite.status === 'transactional_active';

    if (verifiedNow) {
      // Fetch embed install status (one-shot). If it's `installed`,
      // advance. Otherwise hold for the manual flow. The fetch is
      // tenant-scoped server-side; an absent / non-shopify row
      // resolves to `undefined` and we treat that as "no Shopify
      // install attempt was made" → advance normally.
      let cancelled = false;
      void (async (): Promise<void> => {
        try {
          const status = await api.sites.getConnectorStatus(
            activeSite.site_id,
            'shopify',
          );
          if (cancelled) return;
          const embed = status.embed_install_status ?? null;
          setShopifyEmbedStatus(embed);
          // Advance only when the install succeeded OR the site has
          // no Shopify connector at all (the verify was done via
          // snippet / .well-known and OAuth wasn't the proof path).
          // The merchant-acknowledged-manual branch is handled in a
          // separate effect below so it runs even when the fetch
          // is already complete.
          const noShopifyInstall =
            embed === null || embed === 'not_applicable';
          if (embed === 'installed' || noShopifyInstall) {
            if (advancedRef.current) return;
            advancedRef.current = true;
            onNext();
          }
        } catch {
          // The connector-status fetch failed transiently. Default to
          // "no Shopify install attempt" — preserve the prior advance
          // behavior so we never strand the merchant on a transient
          // failure.
          if (cancelled) return;
          if (advancedRef.current) return;
          advancedRef.current = true;
          onNext();
        }
      })();
      return (): void => {
        cancelled = true;
      };
    }

    // Slow path — the dashboard hasn't re-fetched the site since the
    // OAuth callback flipped its status. Wait for the shared poll's
    // `connected` terminal as the trigger to do a one-shot GET. The
    // poll itself does not change `site.status`; we re-read the
    // canonical row here, set it on the active-site provider, and
    // gate the advance on the server's `status` value.
    if (shopifyReturn.connectStatus !== 'connected') return;

    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const fresh = await api.sites.get(activeSite.site_id);
        if (cancelled || fresh === null) return;
        // Update both the list view and the active site so the
        // resume mapping + downstream renders see the new status.
        updateSite(activeSite.site_id, fresh);
        setActiveSite(fresh);
        // Server truth is the gate. If the OAuth callback did NOT
        // record a successful domain match the merchant's
        // `site.status` will still be `pending_proof`; we stay on
        // step 3 and the merchant can take one of the documented
        // fallbacks (re-paste snippet, .well-known, etc.).
        if (
          fresh.status === 'discovery_active'
          || fresh.status === 'transactional_active'
        ) {
          // ADR-0056 — same embed-install-status gate as the fast path.
          try {
            const cstatus = await api.sites.getConnectorStatus(
              fresh.site_id,
              'shopify',
            );
            if (cancelled) return;
            const embed = cstatus.embed_install_status ?? null;
            setShopifyEmbedStatus(embed);
            const noShopifyInstall =
              embed === null || embed === 'not_applicable';
            if (embed === 'installed' || noShopifyInstall) {
              if (advancedRef.current) return;
              advancedRef.current = true;
              onNext();
            }
            // Otherwise (scope_denied / pending_retry / pending) hold
            // for the inline manual-install panel below.
          } catch {
            // Status fetch failed — default to the prior advance
            // behavior so a transient error never strands the merchant.
            if (cancelled) return;
            if (advancedRef.current) return;
            advancedRef.current = true;
            onNext();
          }
        }
      } catch {
        /* swallow — onboarding stays on step 3; the merchant can
           verify via the snippet path or retry Connect Shopify. */
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [
    activeSite,
    api,
    onNext,
    setActiveSite,
    shopifyReturn.connectStatus,
    shopifyReturn.returnedFromOauth,
    updateSite,
  ]);

  // ADR-0056 — when the merchant explicitly acknowledges they've added
  // the embed (the "continue" CTA on the manual-install panel),
  // advance regardless of whether a ping has landed yet. Shopify CDN
  // caches CSS/JS for a few minutes; forcing the merchant to wait for
  // a first-ping confirmation is hostile.
  useEffect(() => {
    if (!manualContinueAcknowledged) return;
    if (advancedRef.current) return;
    advancedRef.current = true;
    onNext();
  }, [manualContinueAcknowledged, onNext]);

  const handleTabChange = (tab: InstallMethod): void => {
    setActiveTab(tab);
  };

  // ADR-0056 — manual-install panel's "I added the embed — check now"
  // handler. Re-fetches the site row; if `last_seen_at` is now set,
  // the InstallDetector polling will catch it and the rest of the
  // flow continues. Honest copy on a miss.
  const handleManualCheckInstalled = async (): Promise<void> => {
    if (activeSite === null || manualChecking) return;
    setManualChecking(true);
    setManualCheckMessage(undefined);
    try {
      const fresh = await api.sites.get(activeSite.site_id);
      if (fresh !== null) {
        updateSite(fresh.site_id, fresh);
        setActiveSite(fresh);
        if (fresh.last_seen_at !== undefined && fresh.last_seen_at !== null) {
          // Embed pinged — advance immediately.
          if (!advancedRef.current) {
            advancedRef.current = true;
            onNext();
          }
          return;
        }
      }
      setManualCheckMessage(
        "We haven't seen a ping yet. Shopify can cache CSS and JS for a"
        + ' few minutes — try again in a moment.',
      );
    } catch {
      setManualCheckMessage(
        "We couldn't check just now. Try again in a moment.",
      );
    } finally {
      setManualChecking(false);
    }
  };

  // ADR-0056 — derived gate for the inline manual-install panel.
  // Render when: the merchant came back from Shopify OAuth, the site
  // is verified, AND the embed install status is one of the non-
  // installed terminals. Treat unknown / null as "no Shopify install
  // attempt was made" — the panel does not surface for those.
  const showManualInstallPanel =
    shopifyReturn.returnedFromOauth
    && activeSite !== null
    && (activeSite.status === 'discovery_active'
        || activeSite.status === 'transactional_active')
    && (shopifyEmbedStatus === 'scope_denied'
        || shopifyEmbedStatus === 'pending_retry'
        || shopifyEmbedStatus === 'pending');

  const domain = activeSite?.domain ?? 'your site';
  const apiDetected: DetectPlatformResult | null =
    detection.kind === 'detected' ? detection.result : null;
  // 2026-10-27 engin8.io launch bug — the readiness scan's own platform
  // read, promoted into the `DetectPlatformResult` shape when (and only
  // when) it adds information the live detect-platform call did NOT. The
  // scan writes `shopify` / `woocommerce` / `wordpress` from unambiguous
  // HTML signals (cdn.shopify.com, wp-content asset URLs, the WordPress
  // generator meta — see services/api/.../probe-homepage.ts) + a longer
  // tail of static-site vendors (`webflow`, `framer`, `ghost`, …) that
  // the install tabs don't carry a dedicated tab for. We only forward
  // the three the install tabs actually DO have tabs for; everything
  // else stays `null` here (the live detection's own `other_known` /
  // `unknown` path handles those cases unchanged).
  const scanPlatform = siteScan.scan?.detected_platform ?? null;
  const scanFallback: DetectPlatformResult | null = useMemo(() => {
    if (scanPlatform === 'shopify') {
      return {
        platform: 'shopify',
        confidence: 'high',
        detected_signals: [{ kind: 'asset_host', value: 'site-scan' }],
        install_artifact: 'shopify_app',
      };
    }
    if (scanPlatform === 'woocommerce') {
      return {
        platform: 'woocommerce',
        confidence: 'high',
        detected_signals: [{ kind: 'plugin_path', value: 'site-scan' }],
        install_artifact: 'woocommerce_plugin',
      };
    }
    if (scanPlatform === 'wordpress') {
      return {
        platform: 'wordpress',
        confidence: 'high',
        detected_signals: [{ kind: 'plugin_path', value: 'site-scan' }],
        install_artifact: 'embed_snippet',
      };
    }
    return null;
  }, [scanPlatform]);

  // Live detection takes precedence when it returned one of the three
  // platforms the install tabs have a dedicated tab for (shopify /
  // woocommerce / wordpress) — a live hit on those is strictly stronger
  // than the scan's HTML-only read. Everything else — `unknown`,
  // `other_known`, `cloudflare` (the CF-fronted-origin-unknown case),
  // and the "still loading / failed / dormant" null state — defers to
  // the scan when it has one of those three platforms to offer. Any CF
  // / CDN axes from the live result are preserved below by spreading
  // apiDetected first.
  const apiHasSpecificPlatform =
    apiDetected !== null
    && (apiDetected.platform === 'shopify'
        || apiDetected.platform === 'woocommerce'
        || apiDetected.platform === 'wordpress');
  const apiWithScanFallback: DetectPlatformResult | null = apiHasSpecificPlatform
    ? apiDetected
    : apiDetected !== null && scanFallback !== null
      // Preserve the live detection's extra axes (cloudflare_proxied,
      // cdn, hosting_hint, detect_warning) while swapping the platform
      // in from the scan — those axes come from headers that `safeFetch`
      // read before any status check, and the scan can't observe them.
      ? { ...apiDetected, ...scanFallback }
      : scanFallback ?? apiDetected;

  // ADR-0052 §4.2.1 — the effective detection passed downstream. When
  // the domain is a myshopify suffix, force `platform: 'shopify'` (and
  // synthesize a minimal high-confidence result if the API call has
  // not resolved or has failed). Non-myshopify domains pass through
  // the raw API result (now scan-fallback-aware) unchanged.
  const detected: DetectPlatformResult | null = isShopifyBySuffix
    ? apiWithScanFallback !== null
      ? { ...apiWithScanFallback, platform: 'shopify' }
      : {
          platform: 'shopify',
          confidence: 'high',
          detected_signals: [
            {
              kind: 'hostname',
              value: shopifyFromDomain ?? activeSite?.domain ?? '',
            },
          ],
          install_artifact: 'shopify_app',
        }
    : apiWithScanFallback;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <NumLabel n={3}>Step 3 · Install and verify</NumLabel>
      {/* Condensed Step-3 header (founder 2026-07-16: was 5 stacked lines).
          Kicker + short h2 + one trust line; the "watch an agent" demo
          moved into the install box's top-right (InstallMethodTabs). */}
      {/* Host character — the metaphor is "the host is waiting at the
          door, hand this install step to let agents in". aria-hidden
          decoration; the step title carries the real meaning. */}
      {/* Condensed header (founder 2026-08-04) — matches SkillsStep. */}
      <h2
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(26px, 4vw, 34px)',
          lineHeight: 1.05,
          color: C.text,
          letterSpacing: -1,
          marginBottom: 32,
        }}
      >
        Add Sill to <em>{domain}</em>.
      </h2>

      {/* ADR-0056 — verified-via-OAuth but embed auto-install failed.
          Sill couldn't write the ScriptTag (scope_denied / OS-2.0
          theme block / transient retry). The merchant is ownership-
          verified, but the storefront has no embed and nothing
          records yet. Surface the 5-step manual-install instructions
          here, in the flow, so they don't land on a "you're live"
          completion that lies. The merchant either pastes + waits
          (InstallDetector catches the first ping) or explicitly
          continues — both paths advance to the completion screen
          honestly. */}
      {showManualInstallPanel ? (
        <div className="mb-4" data-testid="install-verify-shopify-manual">
          <div className="flex items-center" style={{ gap: 8, marginBottom: 8 }}>
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.ink,
                background: C.amber,
                letterSpacing: 1.5,
                padding: '2px 6px',
                borderRadius: 2,
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              ONE MORE STEP
            </span>
            <h4
              style={{
                fontFamily: sansFont,
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.3,
                color: C.text,
                margin: 0,
              }}
            >
              Add the embed
            </h4>
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              lineHeight: 1.55,
              marginBottom: 12,
              maxWidth: '60ch',
            }}
          >
            {domain} is verified. Add the embed snippet below to your
            storefront theme to start collecting agent traffic.
          </p>
          <ShopifyManualInstallPanel
            site_key_hash={activeSite?.site_key_hash}
            onCheckInstalled={handleManualCheckInstalled}
            checking={manualChecking}
            checkResultMessage={manualCheckMessage}
            showRetryAutoInstall={false}
          />
          <button
            type="button"
            onClick={() => setManualContinueAcknowledged(true)}
            className="mt-3 flex items-center gap-2 px-4 py-2"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 2,
              cursor: 'pointer',
            }}
            data-testid="install-verify-shopify-manual-continue"
          >
            I&apos;ll do it later — continue <ArrowRight size={13} />
          </button>
        </div>
      ) : null}

      {/* Detection status — ONLY the loading / no-detection states. When
          detection resolves, `InstallMethodTabs` renders the "We think
          this is …" lead itself (right above the tabs, keyed off the
          RECOMMENDED tab), so this strip intentionally drops its own
          detected branch to avoid a duplicate line (ADR-0197 tabs-on-top,
          founder 2026-07-28). Detection is only ever a hint; the connect
          path is never gated on it. Suffix-derived Shopify (ADR-0052
          §4.2.1) preselects the Shopify tab and shows no strip. */}
      {effectivePresentation === 'tabs'
        && detected === null
        && detection.kind === 'loading'
        && !isShopifyBySuffix ? (
        <div className="flex items-center gap-2 mb-4 flex-wrap" style={{ minHeight: 24 }}>
          <RefreshCw size={13} color={C.muted} style={{ animation: 'spin 1.4s linear infinite' }} />
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Detecting…
          </span>
        </div>
      ) : null}

      {/* The install-method tabs. Detection is forwarded as a hint;
          every tab is clickable regardless of detection outcome.
          Rendered only when we have an active site. For a detected
          Shopify site the Shopify tab is preselected by `preselectTab`
          — the connect panel lives ONCE here (no separate Shopify-first
          card; ADR-0054 §0). */}
      {activeSite !== null ? (
        <Card className="p-5 mb-4">
          <InstallMethodTabs
            detected={detected}
            site={activeSite}
            presentation={effectivePresentation}
            onTabChange={handleTabChange}
            proofCheck={proofCheck}
            onInstalled={() => {
              // Fires only from InstallDetector's first ping (the snippet
              // is live), so verify immediately. A Worker deploy no longer
              // routes through here (ADR-0212) — its proof arrives via
              // onDomainVerified, and its deployed state has its own
              // Verify button for the edge-propagation delay.
              void proofCheck.verify();
            }}
            onDomainVerified={() => {
              // CF Worker deploy recorded proof server-side — refresh the
              // site so the provider has status:discovery_active, then
              // advance without the manual Verify click.
              if (!advancedRef.current) {
                advancedRef.current = true;
                if (activeSite !== null) {
                  void api.sites.get(activeSite.site_id).then((fresh) => {
                    if (fresh !== null) {
                      updateSite(activeSite.site_id, fresh);
                      setActiveSite(fresh);
                    }
                  }).catch(() => { /* swallow — advance regardless */ });
                }
                onNext();
              }
            }}
          />
        </Card>
      ) : null}



      {/* Ownership-proof status surface. NO `onSkip` is passed —
          verify is mandatory per ADR-0049. The ACTUAL Verify control
          lives inline in `ScriptTagInstallPanel` next to the snippet
          (ADR-0050 §1.2 — paste-once / verify-once); this panel
          renders the load/error state, the `.well-known` fallback
          when the homepage scan misses, and a read-only status line.
          The shared `proofCheck` state is the single source of truth;
          a successful verify fires `onVerified → onNext()`.

          ADR-0054 §0 declutter — the panel only renders on snippet
          install tabs (`script_tag` / `wordpress` / `woocommerce`; the
          Woo tab was split from `wordpress` 2026-07-08 but still owns
          a snippet-paste Step 1 that needs the proof surface); on the
          `shopify` tab the proof IS the OAuth connect above, so a
          separate "PROVE DOMAIN OWNERSHIP / WAITING FOR VERIFY"
          surface is irrelevant and confusing. The shared `proofCheck`
          hook stays mounted regardless so the inline Verify in
          `ScriptTagInstallPanel` is untouched. */}
      {/* Also shown for `cloudflare` — after the Worker deploys, the
          merchant still needs to prove ownership. The Worker passes all
          non-special requests to origin unchanged (`return fetch(request)`),
          so the homepage proof check works normally if the origin has the
          snippet; the `.well-known/sill-proof.txt` fallback also works
          (the Worker serves `/.well-known/agent-card.json` only, passes
          everything else through). ADR-0189. */}
      {activeTab === 'script_tag'
        || activeTab === 'wordpress'
        || activeTab === 'woocommerce'
        || activeTab === 'cloudflare' ? (
        <div className="mb-4">
          <ProofStatusPanel onVerified={onNext} proofCheck={proofCheck} />
        </div>
      ) : null}

      {/* Exits — visually separated from the install/verify work above.
          Invite Your Developer is the only exits item (ADR-0168 — the
          evaluator ≠ installer escape hatch). Finish-later moved to the
          header row next to Back (Onboarding.tsx) 2026-10-21. */}
      <div
        className="mt-8 pt-5"
        style={{ borderTop: `1px solid ${C.borderSoft}` }}
      >
        {activeSite !== null ? <InviteDeveloperPanel site={activeSite} /> : null}
      </div>
    </motion.div>
  );
}
